import {injectable, inject, decorate, GlobalContainerTypes, Auth} from '@innowise-group/core';
import EventEmitter from 'events';
import {notificationsManager} from '@innowise-group/ui-kit';
import {FloorsContainerTypes} from '../floors-ioc.types';
import {FloorInboundEvent} from './floor-events';
import {FloorEvents, FloorsEventEmitter} from './floors-event-emitter.types';

decorate(injectable(), EventEmitter);

declare interface FloorsEventEmitterService {
  on<U extends keyof FloorEvents>(event: U, listener: FloorEvents[U]): this;

  emit<U extends keyof FloorEvents>(event: U, ...args: Parameters<FloorEvents[U]>): boolean;
}

class FloorsEventEmitterService extends EventEmitter implements FloorsEventEmitter {
  public static readonly type = FloorsContainerTypes.FloorsEventEmitter;
  public client: WebSocket;
  @inject(GlobalContainerTypes.Auth) private auth: Auth;

  constructor() {
    super();
    this.client = null;
    this.connect = this.connect.bind(this);
    this.check = this.check.bind(this);
    this.close = this.close.bind(this);
  }

  private get websocketProtocol(): 'ws' | 'wss' {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    if (apiBaseUrl?.startsWith('http://')) {
      return 'ws';
    }

    if (apiBaseUrl?.startsWith('https://')) {
      return 'wss';
    }

    const host = this.host;

    if (host.startsWith('localhost') || host.startsWith('127.0.0.1')) {
      return 'ws';
    }

    return 'wss';
  }

  async connect(floorId: string) {
    await this.auth?.updateToken();

    if (this.auth?.authState.accessToken) {
      const websocket = new WebSocket(
        `${this.websocketProtocol}://${this.host}/ws/floor/${floorId}?token=${this.auth?.authState.accessToken}`,
      );
      let connectInterval: NodeJS.Timeout;
      websocket.onopen = () => {
        this.client = websocket;
        this.emit('clientReady');
        clearTimeout(connectInterval);
      };
      websocket.onclose = (event: CloseEvent) => {
        if (event.code === 1011) {
          notificationsManager.error({title: 'networkError', subtitle: 'networkError'});
          connectInterval = setTimeout(() => this.check(floorId), 5000);
        }
        if (!event.wasClean) {
          notificationsManager.error({title: 'networkError', subtitle: 'networkError'});
          connectInterval = setTimeout(() => this.check(floorId), 5000);
        }
      };
      websocket.onerror = () => {
        websocket.close();
      };
      websocket.onmessage = (event: MessageEvent) => {
        const messageData = JSON.parse(event.data);
        this.emit(messageData.event, messageData);
      };
    }
  }

  public get host(): string {
    return process.env.REACT_APP_API_WEBSOCKET_HOST || window.location.host;
  }

  public get isClientReady(): boolean {
    return this.client && this.client.readyState !== WebSocket.CLOSED ? true : false;
  }

  close() {
    if (this.client) {
      this.client.close();
    }
  }

  emit(eventName: string | symbol, ...args: any[]): boolean {
    // Read-only websocket requests still go over the socket.
    if (eventName === FloorInboundEvent.MEETINGROOM_STATUS) {
      const data = {
        event: eventName,
        ...args[0],
      };
      if (this.client && this.client.readyState !== WebSocket.CLOSED) {
        this.client.send(JSON.stringify(data));
      }
    }
    return super.emit(eventName, ...args);
  }

  private check(floorId: string) {
    if (!this.client || this.client.readyState === WebSocket.CLOSED) {
      this.connect(floorId);
    }
  }
}

export default FloorsEventEmitterService;
